import * as m from '@staiter/i18n';
import type { TemplateSnapshot, TemplateSnapshotViolation } from '@staiter/shared/task-module';
import { checkSnapshotGuards } from '@staiter/shared/task-module';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import {
  ChevronDown,
  ChevronUp,
  Folder,
  GitBranch,
  Info,
  ListChecks,
  Plus,
  Save,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { Button, ConfirmDialog, Input, Select, Textarea } from '~/components/primitives';
import { MoveToPopover } from '~/components/tasks/blueprints/editor/MoveToPopover';
import { OffsetStepper } from '~/components/tasks/blueprints/editor/OffsetStepper';
import { TaskDescriptionEditor } from '~/components/tasks/blueprints/editor/TaskDescriptionEditor';
import styles from '~/components/tasks/blueprints/editor/editor.module.css';
import { ApiRequestError } from '~/lib/api/client';
import { formatDateTime } from '~/lib/format';
import {
  fetchBlueprint,
  fetchBlueprintVersions,
  resolveUserName,
  updateBlueprint,
} from '~/lib/tasks/api/blueprints';
import {
  type FlatRow,
  type SnapshotTask,
  flattenSnapshot,
  makeTask,
  moveTask,
  normalizeOffsets,
  removeTaskCascade,
  renumberSortOrder,
  reorderTask,
  setTaskKind,
} from '~/lib/tasks/blueprints/draft';
import { clearDraft, loadDraft, saveDraft } from '~/lib/tasks/blueprints/draftStorage';
import {
  DEPENDENCY_TYPE_LABEL,
  ESTIMATE_UOM_LABEL,
  PROJECT_KIND_LABEL,
  TASK_PRIORITY_LABEL,
  labelOf,
} from '~/lib/tasks/i18n/labels';
import shared from './_auth._shared.module.css';

/**
 * `/work-blueprints/$id/edit` — редактор шаблона (фаза C, SOLUTION §5).
 *
 * Инвариант I-B: правится СНАПШОТ, а не живые таблицы задач — ни одна операция
 * здесь не трогает проекты. Инвариант I-C: время только «День N».
 *
 * Правка append-only: «Сохранить» = PUT целиком → новая версия линии. Поэтому
 * черновик живёт в состоянии страницы (+ localStorage), а не пишется по полю:
 * промежуточные состояния не должны порождать версии.
 */

const LIST_KEY = ['task-module', 'blueprints'];

/** kinds, требующие проекта-источника: в редакторе их предлагать нельзя (§4.3). */
const SOURCE_REQUIRED_KINDS = new Set(['period_close', 'work_order_execution']);

export const Route = createFileRoute('/_auth/work-blueprints_/$id_/edit')({
  component: BlueprintEditPage,
});

/**
 * F-C6 — состояние конфликта версий.
 *
 * Номер активной версии приезжает в `details` ошибки, а имя автора и время —
 * нет: 409 несёт машинные поля, не денорм. Поэтому диалог открывается сразу с
 * номером и дозаполняется из `GET /:id/versions` (там и `created_by_user_id`, и
 * `created_at`, и директория имён из B15) — новая ручка для этого не нужна.
 */
type ConflictState = {
  activeVersion: number;
  /** id активной строки — «сохранить поверх» пишет именно в неё. */
  activeId: string | null;
  authorName: string | null;
  savedAt: string | null;
};

// ─── Валидация ───────────────────────────────────────────────────────

/** Ключи задач, которые гвард пометил как проблемные (для подсветки строк). */
function offendingTaskKeys(violations: readonly TemplateSnapshotViolation[]): Set<string> {
  const out = new Set<string>();
  const push = (v: unknown): void => {
    if (typeof v === 'string') out.add(v);
  };
  for (const v of violations) {
    const d = v.details as Record<string, unknown>;
    for (const key of ['keys', 'cycle_keys', 'missing_parent_keys', 'self_parent_keys']) {
      const arr = d[key];
      if (Array.isArray(arr)) for (const k of arr) push(k);
    }
    for (const key of ['tasks', 'values', 'fields', 'edges', 'milestones']) {
      const arr = d[key];
      if (!Array.isArray(arr)) continue;
      for (const item of arr) {
        if (item && typeof item === 'object') {
          const rec = item as Record<string, unknown>;
          push(rec.key);
          push(rec.from_key);
          push(rec.to_key);
        }
      }
    }
  }
  return out;
}

function BlueprintEditPage(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const detail = useQuery({ queryKey: [...LIST_KEY, id], queryFn: () => fetchBlueprint(id) });

  const [draft, setDraft] = useState<TemplateSnapshot | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('free');
  const [description, setDescription] = useState('');
  const [dirty, setDirty] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** F-C9 — строки, чьи смещения сдвинула последняя нормализация (подсветка). */
  const [shiftedKeys, setShiftedKeys] = useState<ReadonlySet<string>>(new Set());
  const [nameError, setNameError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [serverViolation, setServerViolation] = useState<TemplateSnapshotViolation | null>(null);
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [restorePrompt, setRestorePrompt] = useState<{
    snapshot: TemplateSnapshot;
    savedAt: string | null;
  } | null>(null);
  /**
   * F-C7 — шаблон, созданный с нуля и ещё ни разу не наполненный. Считается
   * ОДИН раз при открытии: после первого добавления задачи снапшот перестанет
   * быть пустым, но сохранение всё ещё будет первым, и ярлык не должен
   * переобуваться под руками.
   */
  const [firstFill, setFirstFill] = useState(false);

  const bp = detail.data;
  const baseVersion = bp?.version ?? null;

  // ── Инициализация черновика (+ предложение восстановить localStorage) ──
  const initialized = useRef(false);
  useEffect(() => {
    if (!bp || initialized.current) return;
    initialized.current = true;
    if (bp.snapshot === null) {
      // v1-формат правится только через конвертацию (§3) — возвращаем на просмотр.
      navigate({ to: '/work-blueprints/$id', params: { id }, replace: true });
      return;
    }
    setName(bp.name);
    setKind(bp.kind);
    setDescription(bp.description ?? '');
    setDraft(bp.snapshot);
    setFirstFill(bp.version === 1 && bp.snapshot.tasks.length === 0);
    const stored = loadDraft(id, bp.version);
    if (stored) setRestorePrompt({ snapshot: stored.snapshot, savedAt: stored.savedAt });
  }, [bp, id, navigate]);

  // ── Персист черновика (debounced) ──
  useEffect(() => {
    if (!draft || !dirty || baseVersion === null) return;
    const t = setTimeout(() => saveDraft(id, baseVersion, draft), 600);
    return () => clearTimeout(t);
  }, [draft, dirty, id, baseVersion]);

  // ── Confirm при уходе ──
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /**
   * Флаг «есть несохранённое» в ref, а не только в state.
   *
   * Блокировщик навигации читает его синхронно, а `setDirty(false)` в
   * onSuccess до редиректа дойти не успевает — успешное сохранение спрашивало
   * «уйти без сохранения?» на ровном месте. Ref снимается в тот же тик, что и
   * решение уйти.
   */
  const dirtyRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useBlocker({
    // beforeunload без этой функции вешается БЕЗУСЛОВНО (дефолт TanStack Router —
    // true): страница без черновика тоже спрашивала «уйти?». Найдено гейтом F27.
    enableBeforeUnload: () => dirtyRef.current,
    shouldBlockFn: () => {
      if (!dirtyRef.current) return false;
      return !window.confirm(m.tmpl_edit_leave_confirm());
    },
  });

  /**
   * Единая точка правки черновика: любая операция проходит нормализацию, и если
   * план сдвинулся — редактор говорит об этом вслух. Молчаливый пересчёт перед
   * отправкой оставлял бы пользователя с числами, которых он не вводил.
   *
   * F-C9 — правило жизни баннера ОДНО и без исключений: каждая правка
   * перезаписывает и сообщение, и подсветку. Сдвига не было → и то, и другое
   * исчезает. Плюс крестик, чтобы убрать сообщение немедленно. Иначе «когда это
   * пропадёт» остаётся вопросом без ответа.
   */
  const applyEdit = useCallback(
    (fn: (s: TemplateSnapshot) => TemplateSnapshot, extraNotice?: string) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const next = fn(prev);
        const { snapshot, shiftedBy, shiftedKeys: moved } = normalizeOffsets(next);
        const messages: string[] = [];
        if (extraNotice) messages.push(extraNotice);
        if (shiftedBy !== 0) {
          messages.push(m.tmpl_edit_normalized({ days: Math.abs(shiftedBy) }));
        }
        setNotice(messages.length > 0 ? messages.join(' ') : null);
        setShiftedKeys(moved);
        return snapshot;
      });
      setDirty(true);
    },
    [],
  );

  const rows: FlatRow[] = useMemo(() => (draft ? flattenSnapshot(draft.tasks) : []), [draft]);
  const taskByKey = useMemo(() => new Map((draft?.tasks ?? []).map((t) => [t.key, t])), [draft]);

  const violations = useMemo(() => (draft ? checkSnapshotGuards(draft) : []), [draft]);
  const allViolations = useMemo(
    () => (serverViolation ? [...violations, serverViolation] : violations),
    [violations, serverViolation],
  );
  const badKeys = useMemo(() => offendingTaskKeys(allViolations), [allViolations]);

  // ── Сохранение ──
  const save = useMutation({
    mutationFn: (opts: { force: boolean; targetId?: string }) => {
      if (!draft) throw new Error('no draft');
      return updateBlueprint(opts.targetId ?? id, {
        name,
        kind,
        description: description || null,
        // Единая шкала sort_order — перед самой отправкой, чтобы порядок в
        // сохранённом снапшоте совпал с тем, что человек видел на экране.
        snapshot: renumberSortOrder(draft),
        ...(opts.force || baseVersion === null ? {} : { base_version: baseVersion }),
      });
    },
    onSuccess: (created) => {
      clearDraft(id, baseVersion ?? 0);
      // Синхронно, до редиректа: иначе блокировщик увидит старое значение.
      dirtyRef.current = false;
      setDirty(false);
      setConflict(null);
      setErrorCode(null);
      setServerViolation(null);
      qc.invalidateQueries({ queryKey: LIST_KEY });
      navigate({ to: '/work-blueprints/$id', params: { id: created.id } });
    },
    onError: (e) => {
      if (!(e instanceof ApiRequestError)) {
        setErrorCode('server.internal');
        return;
      }
      const details = (e.details ?? {}) as Record<string, unknown>;
      /*
       * Два кода — одна ситуация для пользователя: «пока ты правил, шаблон ушёл
       * вперёд». `version_conflict` — если правившаяся строка ещё активна, но
       * base_version разошёлся; `version_stale` — если её уже сменила новая
       * версия. При append-only второй случай и есть типичный исход гонки
       * двух вкладок: сохранение всегда рождает НОВУЮ строку, а прежняя
       * становится неактивной.
       */
      if (
        e.code === 'task_module.blueprint_version_conflict' ||
        e.code === 'task_module.blueprint_version_stale'
      ) {
        setConflict({
          activeVersion: Number(details.active_version ?? 0),
          activeId: null,
          authorName: null,
          savedAt: null,
        });
        void loadConflictContext();
        return;
      }
      if (e.code === 'task_module.blueprint_name_taken') {
        setNameError(m.error_by_code(e.code, details));
        return;
      }
      if (e.code.startsWith('task_module.template_snapshot_')) {
        // Серверный гвард приезжает в ту же подсветку строк, что клиентский:
        // «422 где-то» вместо строки — ровно то, чего мы избегаем.
        setServerViolation({
          code: e.code.replace('task_module.', '') as TemplateSnapshotViolation['code'],
          details,
        });
        return;
      }
      setErrorCode(e.code);
    },
  });

  /**
   * F-C6 — дозаполнение конфликт-диалога: кто и когда сохранил активную версию.
   * Заодно запоминаем её id — «сохранить поверх» пишет ровно в неё, и второй
   * запрос за тем же списком не нужен.
   */
  async function loadConflictContext(): Promise<void> {
    try {
      const list = await fetchBlueprintVersions(id);
      const active = list.versions.find((v) => v.is_active);
      if (!active) return;
      setConflict((prev) =>
        prev === null
          ? prev
          : {
              activeVersion: active.version,
              activeId: active.id,
              authorName: resolveUserName(list.directory, active.created_by_user_id),
              savedAt: active.created_at,
            },
      );
    } catch {
      // Диалог уже открыт и работоспособен с одним номером версии: провал
      // денорма имени не повод его закрывать или показывать вторую ошибку.
    }
  }

  /**
   * «Сохранить поверх»: PUT уходит на АКТИВНУЮ строку линии и без `base_version`.
   *
   * Повтор на прежний id бесполезен — он уже неактивен, и сервер снова ответит
   * `stale`. Актуальный id берём из уже загруженного контекста конфликта, а если
   * тот не доехал — спрашиваем список версий: `details` несёт только номер.
   */
  const saveOntoActive = async (): Promise<void> => {
    let activeId = conflict?.activeId ?? null;
    if (activeId === null) {
      const list = await fetchBlueprintVersions(id);
      activeId = list.versions.find((v) => v.is_active)?.id ?? null;
    }
    if (activeId === null) {
      setErrorCode('task_module.blueprint_not_found');
      setConflict(null);
      return;
    }
    save.mutate({ force: true, targetId: activeId });
  };

  if (detail.isPending || !draft) {
    return (
      <div className={shared.page}>
        <p className={shared.subtle}>{m.tm_loading()}</p>
      </div>
    );
  }

  const selected = selectedKey !== null ? taskByKey.get(selectedKey) : undefined;
  const canSave = violations.length === 0 && name.trim().length > 0;
  // F-C5 — числа живые: правим базовую версию, сохранение родит следующую.
  const nextVersion = (baseVersion ?? 0) + 1;
  const saveLabel = firstFill ? m.tmpl_edit_save_first() : m.tmpl_edit_save();
  const saveHint = firstFill
    ? m.tmpl_edit_save_hint_first({ next: nextVersion })
    : m.tmpl_edit_save_hint({ next: nextVersion });

  const addTask = (taskKind: 'task' | 'folder'): void => {
    const key = crypto.randomUUID();
    const maxOrder = draft.tasks.reduce((mx, t) => Math.max(mx, t.sort_order), 0);
    applyEdit((s) => ({
      ...s,
      tasks: [
        ...s.tasks,
        makeTask(
          key,
          taskKind === 'folder' ? m.tmpl_edit_new_folder() : m.tmpl_edit_new_task(),
          taskKind,
          null,
          maxOrder + 10,
        ),
      ],
    }));
    setSelectedKey(key);
  };

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.tm_route_project_templates(), to: '/work-blueprints' },
              { label: bp?.name ?? '', to: `/work-blueprints/${id}` },
              { label: m.tmpl_edit_title() },
            ]}
          />
        }
        title={m.tmpl_edit_title()}
        actions={
          <div className={shared.rowCenter}>
            {/* F-C5 — какую версию правим: append-only-модель должна читаться с
                экрана, а не всплывать впервые в конфликтном диалоге. */}
            <span className={styles.versionBadge}>
              <GitBranch size={12} aria-hidden="true" />
              {m.tmpl_edit_editing_version({ version: baseVersion ?? 0 })}
            </span>
            <Link to="/work-blueprints/$id" params={{ id }}>
              <Button variant="ghost">{m.tm_cancel()}</Button>
            </Link>
            {/* F-C11 — primary на экране один, и он в липкой панели внизу: она
                видна всегда и стоит рядом с «есть несохранённые правки». Верхняя
                кнопка остаётся как дубль для длинных шаблонов, но тише. */}
            <Button
              variant="secondary"
              leadingIcon={<Save size={14} />}
              loading={save.isPending}
              disabled={!canSave}
              onClick={() => save.mutate({ force: false })}
            >
              {saveLabel}
            </Button>
          </div>
        }
      />

      {/* Свойства шаблона (F9) */}
      <section className={styles.sectionCard}>
        <div className={styles.propsRow}>
          <Input
            label={m.tmpl_field_template_name()}
            value={name}
            error={nameError ?? undefined}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
              setDirty(true);
            }}
          />
          <Select
            label={m.tmpl_view_meta_kind()}
            value={kind}
            options={Object.keys(PROJECT_KIND_LABEL)
              .filter((k) => !SOURCE_REQUIRED_KINDS.has(k))
              .map((k) => ({ value: k, label: labelOf(PROJECT_KIND_LABEL, k as never) }))}
            onChange={(e) => {
              setKind(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <Textarea
          label={m.tmpl_field_description()}
          value={description}
          rows={2}
          onChange={(e) => {
            setDescription(e.target.value);
            setDirty(true);
          }}
        />
      </section>

      {notice && (
        <div className={styles.noticeInfo}>
          <Info size={16} aria-hidden="true" />
          <span>{notice}</span>
          <button
            type="button"
            className={styles.noticeClose}
            aria-label={m.tmpl_edit_notice_dismiss()}
            title={m.tmpl_edit_notice_dismiss()}
            onClick={() => {
              setNotice(null);
              setShiftedKeys(new Set());
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {allViolations.length > 0 && (
        <div className={styles.validationBox} role="alert">
          <span className={styles.validationTitle}>{m.tmpl_edit_validation_title()}</span>
          <ul className={styles.validationList}>
            {allViolations.map((v) => (
              <li key={v.code} className={styles.validationItem}>
                {m.error_by_code(`task_module.${v.code}`, v.details as never)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Дерево (F7 / F8) */}
      <section className={styles.sectionCard}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionTitle}>{m.tmpl_view_section_tasks()}</span>
          <span className={styles.spacer} />
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Plus size={14} />}
            onClick={() => addTask('task')}
          >
            {m.tmpl_edit_add_task()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Folder size={14} />}
            onClick={() => addTask('folder')}
          >
            {m.tmpl_edit_add_folder()}
          </Button>
        </div>

        {rows.length === 0 ? (
          <p className={styles.emptyHint}>{m.tmpl_edit_empty_hint()}</p>
        ) : (
          <>
            <div className={styles.headGrid} aria-hidden="true">
              <span>{m.tmpl_view_col_task()}</span>
              <span className={styles.headStepper}>{m.tmpl_view_col_start()}</span>
              <span className={styles.headStepper}>{m.tmpl_view_col_due()}</span>
              <span className={styles.headNum}>{m.tmpl_view_col_duration()}</span>
              <span />
              <span />
            </div>
            <ul className={shared.resetList} role="tree" aria-label={m.tmpl_view_section_tasks()}>
              {rows.map(({ task, depth }) => (
                <li key={task.key} role="treeitem" aria-level={depth + 1}>
                  <TaskRow
                    task={task}
                    depth={depth}
                    rows={rows}
                    invalid={badKeys.has(task.key)}
                    shifted={shiftedKeys.has(task.key)}
                    selected={task.key === selectedKey}
                    onSelect={() => setSelectedKey(task.key === selectedKey ? null : task.key)}
                    applyEdit={applyEdit}
                  />
                  {task.key === selectedKey && selected && (
                    <TaskPropsPanel task={selected} applyEdit={applyEdit} />
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <MilestonesEditor
        draft={draft}
        rows={rows}
        applyEdit={applyEdit}
        badKeys={badKeys}
        shiftedKeys={shiftedKeys}
      />
      <DependenciesEditor draft={draft} rows={rows} applyEdit={applyEdit} badKeys={badKeys} />

      <div className={styles.stickyBar}>
        {dirty && <span className={styles.dirtyMark}>{m.tmpl_edit_unsaved()}</span>}
        {errorCode && <span className={shared.dangerText}>{m.error_by_code(errorCode)}</span>}
        <span className={styles.spacer} />
        {/* F-C5/F-C7 — что именно произойдёт по нажатию: номер будущей версии
            рядом с кнопкой, а не в голове пользователя. */}
        <span className={styles.saveHint}>{saveHint}</span>
        <Button
          leadingIcon={<Save size={14} />}
          loading={save.isPending}
          disabled={!canSave}
          onClick={() => save.mutate({ force: false })}
        >
          {saveLabel}
        </Button>
      </div>

      {restorePrompt && (
        <ConfirmDialog
          open
          // F-C3 — sm (400px) не вмещал два длинных ярлыка футера, и левый уезжал
          // за край. Диалог с двумя равноправными исходами обязан показывать оба.
          size="md"
          title={m.tmpl_edit_restore_draft_title()}
          // F-C8 — когда черновик записан: пятиминутный и недельный требуют
          // разных решений, а без времени они выглядят одинаково.
          meta={
            restorePrompt.savedAt !== null
              ? m.tmpl_edit_restore_draft_saved_at({ at: formatDateTime(restorePrompt.savedAt) })
              : undefined
          }
          description={`${m.tmpl_edit_restore_draft_body()} ${m.tmpl_edit_restore_draft_discard_warning()}`}
          confirmLabel={m.tmpl_edit_restore_draft_confirm()}
          cancelLabel={m.tmpl_edit_restore_draft_discard()}
          onConfirm={() => {
            setDraft(restorePrompt.snapshot);
            setDirty(true);
            setRestorePrompt(null);
          }}
          onCancel={() => {
            clearDraft(id, baseVersion ?? 0);
            setRestorePrompt(null);
          }}
          /* F-C13 — крестик здесь НЕ равен «Отмена»: отказ удаляет черновик, и
             закрывать вопрос крестиком значило бы уничтожить правки, не ответив.
             Закрытие просто убирает диалог — черновик остаётся на диске и будет
             предложен снова. */
          onDismiss={() => setRestorePrompt(null)}
        />
      )}

      {conflict && (
        <ConfirmDialog
          open
          size="md"
          title={m.tmpl_edit_conflict_title()}
          /* F-C6 — кто и когда: без этого «сохранить поверх» решается вслепую.
             Пока денорм едет, строки просто нет — заглушка «загрузка…» в
             диалоге решения хуже её отсутствия. */
          meta={
            conflict.savedAt !== null ? (
              <span className={styles.conflictMeta}>
                <span>
                  {conflict.authorName !== null
                    ? m.tmpl_edit_conflict_author({
                        user: conflict.authorName,
                        at: formatDateTime(conflict.savedAt),
                      })
                    : m.tmpl_edit_conflict_author_unknown({
                        at: formatDateTime(conflict.savedAt),
                      })}
                </span>
              </span>
            ) : undefined
          }
          description={`${m.tmpl_edit_conflict_body({
            active: conflict.activeVersion,
            next: conflict.activeVersion + 1,
          })} ${m.tmpl_edit_conflict_cancel_hint()}`}
          confirmLabel={m.tmpl_edit_conflict_confirm()}
          cancelLabel={m.tm_cancel()}
          loading={save.isPending}
          onConfirm={() => void saveOntoActive()}
          onCancel={() => setConflict(null)}
        />
      )}
    </div>
  );
}

// ─── Строка дерева ───────────────────────────────────────────────────

function TaskRow({
  task,
  depth,
  rows,
  invalid,
  shifted,
  selected,
  onSelect,
  applyEdit,
}: {
  task: SnapshotTask;
  depth: number;
  rows: FlatRow[];
  invalid: boolean;
  /** F-C9 — смещения этой строки только что подвинула нормализация. */
  shifted: boolean;
  selected: boolean;
  onSelect: () => void;
  applyEdit: (fn: (s: TemplateSnapshot) => TemplateSnapshot, notice?: string) => void;
}): JSX.Element {
  const isFolder = task.kind === 'folder';
  const start = task.start_offset_days;
  const due = task.due_offset_days;
  const inverted = start !== null && due !== null && due < start;

  const setOffset = (
    field: 'start_offset_days' | 'due_offset_days',
    next: (current: number | null) => number | null,
  ): void =>
    applyEdit((s) => ({
      ...s,
      // Значение берётся из АКТУАЛЬНОГО снапшота, а не из пропа строки: серия
      // быстрых кликов по «+» иначе считала бы от одного и того же старого числа.
      tasks: s.tasks.map((t) => (t.key === task.key ? { ...t, [field]: next(t[field]) } : t)),
    }));

  return (
    <div className={[styles.rowGrid, invalid ? styles.rowInvalid : ''].filter(Boolean).join(' ')}>
      <div className={styles.titleCell}>
        {Array.from({ length: depth }, (_, i) => (
          <span key={`${task.key}-g-${i}`} className={styles.indentGuide} aria-hidden="true" />
        ))}
        <button
          type="button"
          className={styles.kindBtn}
          aria-label={isFolder ? m.tmpl_edit_kind_to_task() : m.tmpl_edit_kind_to_folder()}
          title={isFolder ? m.tmpl_edit_kind_to_task() : m.tmpl_edit_kind_to_folder()}
          onClick={() =>
            applyEdit(
              (s) => {
                const out = setTaskKind(s, task.key, isFolder ? 'task' : 'folder');
                return out.snapshot;
              },
              isFolder ? undefined : m.tmpl_edit_folder_cleared(),
            )
          }
        >
          {isFolder ? (
            <Folder size={14} aria-hidden="true" />
          ) : (
            <ListChecks size={14} aria-hidden="true" />
          )}
        </button>
        <input
          className={[styles.titleInput, isFolder ? styles.titleInputFolder : '']
            .filter(Boolean)
            .join(' ')}
          value={task.title}
          aria-label={m.tmpl_edit_task_title()}
          onChange={(e) =>
            applyEdit((s) => ({
              ...s,
              tasks: s.tasks.map((t) => (t.key === task.key ? { ...t, title: e.target.value } : t)),
            }))
          }
        />
      </div>

      <OffsetStepper
        value={start}
        disabled={isFolder}
        shifted={shifted}
        ariaLabel={m.tmpl_view_col_start()}
        onChange={(next) => setOffset('start_offset_days', next)}
      />
      <OffsetStepper
        value={due}
        disabled={isFolder}
        invalid={inverted}
        shifted={shifted}
        ariaLabel={m.tmpl_view_col_due()}
        onChange={(next) => setOffset('due_offset_days', next)}
      />
      <span className={styles.durationCell}>
        {start !== null && due !== null && !inverted
          ? m.tmpl_view_duration({ days: due - start + 1 })
          : '—'}
      </span>

      <span />

      <div className={styles.rowActions}>
        <button
          type="button"
          className={styles.rowIconBtn}
          aria-label={m.tmpl_edit_move_up()}
          onClick={() => applyEdit((s) => reorderTask(s, task.key, -1))}
        >
          <ChevronUp size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.rowIconBtn}
          aria-label={m.tmpl_edit_move_down()}
          onClick={() => applyEdit((s) => reorderTask(s, task.key, 1))}
        >
          <ChevronDown size={14} aria-hidden="true" />
        </button>
        <MoveToPopover
          taskKey={task.key}
          rows={rows}
          currentParent={task.parent_key}
          onMove={(parent) => applyEdit((s) => moveTask(s, task.key, parent))}
        />
        <button
          type="button"
          className={styles.rowIconBtn}
          aria-label={m.tmpl_edit_props()}
          aria-expanded={selected}
          onClick={onSelect}
        >
          <Settings2 size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.rowIconBtnDanger}
          aria-label={m.tmpl_edit_delete_task()}
          onClick={() => applyEdit((s) => removeTaskCascade(s, task.key))}
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// ─── Панель свойств задачи (F9) ──────────────────────────────────────

function TaskPropsPanel({
  task,
  applyEdit,
}: {
  task: SnapshotTask;
  applyEdit: (fn: (s: TemplateSnapshot) => TemplateSnapshot, notice?: string) => void;
}): JSX.Element {
  const patch = (fields: Partial<SnapshotTask>): void =>
    applyEdit((s) => ({
      ...s,
      tasks: s.tasks.map((t) => (t.key === task.key ? { ...t, ...fields } : t)),
    }));

  return (
    <div className={styles.propsPanel}>
      <div className={styles.propsRow}>
        <Select
          label={m.tm_priority()}
          value={task.priority}
          options={Object.keys(TASK_PRIORITY_LABEL).map((p) => ({
            value: p,
            label: labelOf(TASK_PRIORITY_LABEL, p as never),
          }))}
          onChange={(e) => patch({ priority: e.target.value as SnapshotTask['priority'] })}
        />
        <Input
          label={m.tmpl_edit_estimate_value()}
          value={task.estimate_value ?? ''}
          disabled={task.kind === 'folder'}
          onChange={(e) => {
            const v = e.target.value.trim();
            // Оценка — пара: значение без единицы схема не принимает, поэтому
            // единица подставляется сразу, а очистка значения снимает и её.
            patch(
              v === ''
                ? { estimate_value: null, estimate_uom: null }
                : { estimate_value: v, estimate_uom: task.estimate_uom ?? 'hours' },
            );
          }}
        />
        <Select
          label={m.tmpl_edit_estimate_uom()}
          value={task.estimate_uom ?? 'hours'}
          disabled={task.estimate_value === null}
          options={Object.keys(ESTIMATE_UOM_LABEL).map((u) => ({
            value: u,
            label: labelOf(ESTIMATE_UOM_LABEL, u as never),
          }))}
          onChange={(e) => patch({ estimate_uom: e.target.value as SnapshotTask['estimate_uom'] })}
        />
      </div>

      {task.assignees.length > 0 && (
        <div className={styles.propsField}>
          <span className={styles.propsLabel}>{m.tmpl_edit_assignees()}</span>
          <div className={styles.chipsRow}>
            {task.assignees.map((a, i) => (
              <span key={`${task.key}-a-${i}`} className={styles.assigneeChip}>
                {a.type === 'role'
                  ? m.tmpl_view_assignee_role({ role: a.role_key })
                  : a.type === 'group'
                    ? m.tmpl_view_assignee_group()
                    : m.tmpl_view_former_member()}
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={m.tmpl_edit_remove_assignee()}
                  onClick={() => patch({ assignees: task.assignees.filter((_, idx) => idx !== i) })}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className={styles.propsField}>
        <span className={styles.propsLabel}>{m.tmpl_field_description()}</span>
        <TaskDescriptionEditor
          json={task.description_json}
          onChange={(json, text) => patch({ description_json: json, description_text: text })}
        />
      </div>
    </div>
  );
}

// ─── Вехи (F10) ──────────────────────────────────────────────────────

function MilestonesEditor({
  draft,
  rows,
  applyEdit,
  badKeys,
  shiftedKeys,
}: {
  draft: TemplateSnapshot;
  rows: FlatRow[];
  applyEdit: (fn: (s: TemplateSnapshot) => TemplateSnapshot, notice?: string) => void;
  badKeys: Set<string>;
  shiftedKeys: ReadonlySet<string>;
}): JSX.Element {
  return (
    <section className={styles.sectionCard}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{m.tmpl_view_section_milestones()}</span>
        <span className={styles.spacer} />
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Plus size={14} />}
          onClick={() =>
            applyEdit((s) => ({
              ...s,
              milestones: [
                ...s.milestones,
                {
                  key: crypto.randomUUID(),
                  name: m.tmpl_edit_new_milestone(),
                  position: s.milestones.length,
                  deadline_offset_days: null,
                  billing_pct: null,
                  linked_task_key: null,
                },
              ],
            }))
          }
        >
          {m.tmpl_edit_add_milestone()}
        </Button>
      </div>

      {draft.milestones.length === 0 ? (
        <p className={styles.emptyHint}>{m.tmpl_edit_no_milestones()}</p>
      ) : (
        draft.milestones.map((ms) => {
          const patch = (fields: Partial<typeof ms>): void =>
            applyEdit((s) => ({
              ...s,
              milestones: s.milestones.map((x) => (x.key === ms.key ? { ...x, ...fields } : x)),
            }));
          return (
            <div
              key={ms.key}
              className={[styles.itemRow, badKeys.has(ms.key) ? styles.itemRowInvalid : '']
                .filter(Boolean)
                .join(' ')}
            >
              <Input
                label={m.tmpl_edit_milestone_name()}
                hideLabel
                className={styles.midInput}
                value={ms.name}
                onChange={(e) => patch({ name: e.target.value })}
              />
              <OffsetStepper
                value={ms.deadline_offset_days}
                shifted={shiftedKeys.has(ms.key)}
                ariaLabel={m.tmpl_view_col_due()}
                onChange={(next) =>
                  applyEdit((s) => ({
                    ...s,
                    milestones: s.milestones.map((x) =>
                      x.key === ms.key
                        ? { ...x, deadline_offset_days: next(x.deadline_offset_days) }
                        : x,
                    ),
                  }))
                }
              />
              {/* F-C12 — «%» как единица измерения справа от числа, а не вместо
                  него: placeholder-«%» исчезал при вводе, и колонка переставала
                  объяснять, что за число в ней стоит. */}
              <span className={styles.pctField}>
                <Input
                  label={m.tmpl_edit_billing_pct()}
                  hideLabel
                  placeholder="0"
                  className={styles.narrowInput}
                  value={ms.billing_pct ?? ''}
                  onChange={(e) => patch({ billing_pct: e.target.value.trim() || null })}
                />
                <span className={styles.pctSuffix} aria-hidden="true">
                  %
                </span>
              </span>
              <Select
                label={m.tmpl_edit_milestone_link()}
                hideLabel
                value={ms.linked_task_key ?? ''}
                placeholder={m.tmpl_edit_milestone_no_link()}
                options={[
                  { value: '', label: m.tmpl_edit_milestone_no_link() },
                  ...rows.map((r) => ({ value: r.task.key, label: r.task.title })),
                ]}
                onChange={(e) => patch({ linked_task_key: e.target.value || null })}
              />
              <button
                type="button"
                className={styles.rowIconBtnDanger}
                aria-label={m.tmpl_edit_delete_milestone()}
                onClick={() =>
                  applyEdit((s) => ({
                    ...s,
                    milestones: s.milestones.filter((x) => x.key !== ms.key),
                  }))
                }
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
          );
        })
      )}
    </section>
  );
}

// ─── Зависимости (F10) ───────────────────────────────────────────────

function DependenciesEditor({
  draft,
  rows,
  applyEdit,
  badKeys,
}: {
  draft: TemplateSnapshot;
  rows: FlatRow[];
  applyEdit: (fn: (s: TemplateSnapshot) => TemplateSnapshot, notice?: string) => void;
  badKeys: Set<string>;
}): JSX.Element {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [relation, setRelation] = useState('blocked_by');

  const duplicate =
    from !== '' &&
    to !== '' &&
    draft.dependencies.some((d) => d.from_key === from && d.to_key === to);
  const selfEdge = from !== '' && from === to;
  const canAdd = from !== '' && to !== '' && !duplicate && !selfEdge;

  return (
    <section className={styles.sectionCard}>
      <span className={styles.sectionTitle}>{m.tmpl_view_section_deps()}</span>

      <div className={styles.itemRow}>
        <Select
          label={m.tmpl_edit_dep_from()}
          hideLabel
          value={from}
          placeholder={m.tmpl_edit_dep_from()}
          options={rows.map((r) => ({ value: r.task.key, label: r.task.title }))}
          onChange={(e) => setFrom(e.target.value)}
        />
        <span aria-hidden="true">→</span>
        <Select
          label={m.tmpl_edit_dep_to()}
          hideLabel
          value={to}
          placeholder={m.tmpl_edit_dep_to()}
          options={rows.map((r) => ({ value: r.task.key, label: r.task.title }))}
          onChange={(e) => setTo(e.target.value)}
        />
        <Select
          label={m.tmpl_edit_dep_type()}
          hideLabel
          value={relation}
          options={Object.keys(DEPENDENCY_TYPE_LABEL).map((r) => ({
            value: r,
            label: labelOf(DEPENDENCY_TYPE_LABEL, r as never),
          }))}
          onChange={(e) => setRelation(e.target.value)}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!canAdd}
          leadingIcon={<Plus size={14} />}
          onClick={() => {
            applyEdit((s) => ({
              ...s,
              dependencies: [
                ...s.dependencies,
                {
                  from_key: from,
                  to_key: to,
                  relation_type: relation,
                } as TemplateSnapshot['dependencies'][number],
              ],
            }));
            setFrom('');
            setTo('');
          }}
        >
          {m.tmpl_edit_add_dep()}
        </Button>
        {/* Дубль ребра блокируется ДО отправки: тот же дефект гвард поймал бы на
            сохранении, но объяснять его пришлось бы задним числом. */}
        {duplicate && <span className={shared.dangerText}>{m.tmpl_edit_dep_duplicate()}</span>}
        {selfEdge && <span className={shared.dangerText}>{m.tmpl_edit_dep_self()}</span>}
      </div>

      {draft.dependencies.length === 0 ? (
        <p className={styles.emptyHint}>{m.tmpl_edit_no_deps()}</p>
      ) : (
        draft.dependencies.map((d, i) => (
          <div
            key={`${d.from_key}-${d.to_key}-${i}`}
            className={[
              styles.itemRow,
              badKeys.has(d.from_key) || badKeys.has(d.to_key) ? styles.itemRowInvalid : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span>{rows.find((r) => r.task.key === d.from_key)?.task.title ?? d.from_key}</span>
            <span aria-hidden="true">→</span>
            <span>{rows.find((r) => r.task.key === d.to_key)?.task.title ?? d.to_key}</span>
            <span className={shared.subtle}>
              {labelOf(DEPENDENCY_TYPE_LABEL, d.relation_type as never)}
            </span>
            <button
              type="button"
              className={styles.rowIconBtnDanger}
              aria-label={m.tmpl_edit_delete_dep()}
              onClick={() =>
                applyEdit((s) => ({
                  ...s,
                  dependencies: s.dependencies.filter((_, idx) => idx !== i),
                }))
              }
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          </div>
        ))
      )}
    </section>
  );
}
